import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/globals.css";
import App from "./App";
import { initTelemetry } from "./transport/telemetryService";
import { initSentry, SentryErrorBoundary } from "./observability/sentry";

initSentry();
initTelemetry();

const container = document.getElementById("root");
if (!container) {
  throw new Error("MACDM mount target #root not found in index.html");
}

/**
 * Yeni sürüm yayınlandığında açık kalan sekmeyi kurtarır.
 *
 * Vite her derlemede parça adlarına içerik hash'i basar. Sekme açıkken
 * deploy olursa, ekranda duran eski `index.html` artık var olmayan bir
 * parçayı ister; `import()` reddedilir ve React ağacı çöker. Operatör
 * bunu iki kez yaşadı: `FFTRadialVisualizer-<hash>.js` 404 döndü ve
 * konsey ekranı komple gitti.
 *
 * Sunucu tarafında eski parçalara geçiş süresi tanıdık; bu ikinci savunma
 * hattı, o pencereyi de kaçıran sekmeler için. Yenileme SADECE bir kez
 * denenir: `sessionStorage` bayrağı, gerçekten bozuk bir sürümde sonsuz
 * yenileme döngüsüne girmeyi engeller.
 */
const CHUNK_RELOAD_KEY = "macdm:chunk-reload";
// İki kurtarma denemesi arasındaki en kısa süre. İlk sürümde bayrak
// yüklemeden 5 sn sonra siliniyordu; tarayıcı testinde ard arda gelen iki
// parça hatası iki ayrı yenileme tetikledi — gerçekten bozuk bir sürümde
// bu sonsuz döngü demekti.
const RELOAD_COOLDOWN_MS = 60_000;
// Bu pencere içinde izin verilen toplam deneme. Aşılırsa kurtarma bırakılır
// ve operatöre açıklayıcı ekran gösterilir; sessizce dönüp durmaktansa bir
// kez "yeni sürüme geç" demek dürüsttür.
const RELOAD_MAX_ATTEMPTS = 2;
const RELOAD_WINDOW_MS = 10 * 60_000;

function isStaleChunkError(reason: unknown): boolean {
  const msg =
    reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function readReloadGuard(): { count: number; last: number } {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return { count: 0, last: 0 };
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof (parsed as { count?: unknown }).count === "number" &&
      typeof (parsed as { last?: unknown }).last === "number"
    ) {
      return parsed as { count: number; last: number };
    }
  } catch {
    // Bozuk/eski biçim — sıfırdan say.
  }
  return { count: 0, last: 0 };
}

function recoverFromStaleChunk(reason: unknown): boolean {
  if (!isStaleChunkError(reason)) return false;
  const now = Date.now();
  const guard = readReloadGuard();
  const count = now - guard.last > RELOAD_WINDOW_MS ? 0 : guard.count;
  if (now - guard.last < RELOAD_COOLDOWN_MS) return false;
  if (count >= RELOAD_MAX_ATTEMPTS) return false;
  sessionStorage.setItem(
    CHUNK_RELOAD_KEY,
    JSON.stringify({ count: count + 1, last: now }),
  );
  window.location.reload();
  return true;
}

window.addEventListener("unhandledrejection", (event) => {
  if (recoverFromStaleChunk(event.reason)) event.preventDefault();
});

/**
 * Çökme ekranı.
 *
 * Eskiden koşulsuz "Hata otomatik olarak bildirildi" yazıyordu. Sentry
 * yalnızca `VITE_SENTRY_DSN` derlemeye gömülmüşse çalışır; gömülü değilse
 * hiçbir yere bildirim gitmiyor ve operatöre yanlış güvence veriliyordu —
 * kimse ayrıntıyı kaydetmeden ekranı kapatıyordu. Artık hata metni ekranda
 * duruyor, böylece ekran görüntüsü tek başına teşhis için yeterli oluyor.
 */
function CrashFallback({ error }: { error?: unknown }) {
  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : error
        ? String(error)
        : "";
  const reported = Boolean(import.meta.env.VITE_SENTRY_DSN);
  // Bayat parça hatası bir çökme değil, sürüm atlamasıdır. Otomatik
  // kurtarma bir kez denendikten sonra buraya düşülürse, operatöre
  // "sistem bozuldu" demek yanlış olur.
  const stale = isStaleChunkError(error);
  if (stale) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4">
          <p className="text-lg font-semibold text-slate-100">
            Yeni sürüm yayınlandı
          </p>
          <p className="text-sm text-slate-400">
            Bu sekme önceki sürümü çalıştırıyordu. Yenileyince kaldığın
            yerden devam edebilirsin — tartışma verisi sunucuda duruyor.
          </p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              window.location.reload();
            }}
            className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-200"
          >
            Yeni sürüme geç
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
      <div className="max-w-lg text-center space-y-4">
        <p className="text-lg font-semibold text-slate-100">
          Beklenmeyen bir hata oluştu
        </p>
        <p className="text-sm text-slate-400">
          Karar Zekası bu ekranı yüklerken çöktü.{" "}
          {reported
            ? "Hata otomatik olarak bildirildi — sayfayı yenileyerek devam edebilirsiniz."
            : "Sayfayı yenileyerek devam edebilirsin; aşağıdaki hata metnini paylaşırsan nedenini bulabiliriz."}
        </p>
        {detail && (
          <pre className="max-h-40 overflow-auto rounded-md bg-slate-900 px-3 py-2 text-left text-xs text-rose-300">
            {detail}
          </pre>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-slate-200"
        >
          Sayfayı yenile
        </button>
      </div>
    </div>
  );
}

createRoot(container).render(
  <StrictMode>
    <SentryErrorBoundary
      fallback={({ error }: { error: unknown }) => (
        <CrashFallback error={error} />
      )}
    >
      <App />
    </SentryErrorBoundary>
  </StrictMode>,
);
